import { useState, type FormEvent, type ReactNode } from 'react';
import { FileText, MessageSquareQuote, Newspaper, Plus, Search, Star, Trash2 } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import {
  CONTENT_STATUSES,
  useBlogTags,
  useCmsPage,
  useDeleteFaq,
  useDeletePage,
  useDeletePost,
  useDeleteTestimonial,
  useFaqCategories,
  useFaqs,
  usePages,
  usePost,
  usePosts,
  useSaveFaq,
  useSavePage,
  useSavePost,
  useSaveTestimonial,
  useTestimonials,
  type BlogPost,
  type CmsPage,
  type ContentStatus,
  type Faq,
  type PostState,
  type Testimonial,
} from '@/lib/cms-api';
import { cn, errorMessage, fieldErrors, formatDateTime, formatNumber } from '@/lib/utils';

/**
 * Site content: pages, blog, FAQs and testimonials (spec §38).
 *
 * One screen with four tabs rather than four screens: they are small tables
 * edited by the same person in the same sitting, and four sidebar entries for
 * eight FAQs is navigation for its own sake.
 *
 * The blog's **state** column is not its status. A post can be `PUBLISHED` with
 * a date next month, which is how scheduling works here — the storefront query
 * already refuses a post whose `publishedAt` has not arrived, so no job runner
 * is involved. `SCHEDULED` says that out loud; `PUBLISHED` on its own would
 * read as live.
 */

type Tab = 'pages' | 'blog' | 'faqs' | 'testimonials';

const TABS: Array<{ id: Tab; label: string; icon: typeof FileText }> = [
  { id: 'pages', label: 'Pages', icon: FileText },
  { id: 'blog', label: 'Blog', icon: Newspaper },
  { id: 'faqs', label: 'FAQs', icon: MessageSquareQuote },
  { id: 'testimonials', label: 'Testimonials', icon: Star },
];

const STATE_TONE: Record<PostState, 'success' | 'warning' | 'neutral'> = {
  LIVE: 'success',
  SCHEDULED: 'warning',
  DRAFT: 'neutral',
  ARCHIVED: 'neutral',
};

export function SiteContentPage() {
  const [tab, setTab] = useState<Tab>('pages');

  return (
    <div>
      <PageHeader
        title="Site Content"
        description="Everything the storefront reads and nobody could edit until now."
      />

      <Card className="mb-4 p-2">
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Content type">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              onClick={() => setTab(entry.id)}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                tab === entry.id
                  ? 'bg-brand-600 text-white'
                  : 'text-ink-muted hover:bg-canvas hover:text-ink',
              )}
            >
              <entry.icon className="size-4" aria-hidden="true" />
              {entry.label}
            </button>
          ))}
        </div>
      </Card>

      {tab === 'pages' && <PagesTab />}
      {tab === 'blog' && <BlogTab />}
      {tab === 'faqs' && <FaqsTab />}
      {tab === 'testimonials' && <TestimonialsTab />}
    </div>
  );
}

/* ==========================================================================
   Shared bits
   ========================================================================== */

function Toolbar({
  search,
  onSearch,
  onCreate,
  createLabel,
  children,
}: {
  search: string;
  onSearch: (value: string) => void;
  onCreate: () => void;
  createLabel: string;
  children?: ReactNode;
}) {
  return (
    <Card className="mb-4 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Search"
            className="pl-9"
            aria-label="Search content"
          />
        </div>

        {children}

        <Button onClick={onCreate}>
          <Plus className="size-4" aria-hidden="true" />
          {createLabel}
        </Button>
      </div>
    </Card>
  );
}

function ListShell({
  isLoading,
  isError,
  error,
  isEmpty,
  emptyTitle,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isEmpty: boolean;
  emptyTitle: string;
  children: ReactNode;
}) {
  if (isLoading) {
    return (
      <Card>
        <div className="space-y-2 p-4">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-12" />
          ))}
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div className="p-4">
          <ErrorAlert title="Could not load this list">{errorMessage(error)}</ErrorAlert>
        </div>
      </Card>
    );
  }

  if (isEmpty) {
    return (
      <Card>
        <EmptyState title={emptyTitle} description="Nothing matches, or nothing exists yet." />
      </Card>
    );
  }

  return <Card>{children}</Card>;
}

function StatusBadge({ status }: { status: ContentStatus }) {
  return (
    <Badge tone={status === 'PUBLISHED' ? 'success' : status === 'DRAFT' ? 'warning' : 'neutral'}>
      {status.toLowerCase()}
    </Badge>
  );
}

function DeleteButton({
  label,
  onDelete,
  disabledReason,
}: {
  label: string;
  onDelete: () => void;
  disabledReason?: string;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={`Delete ${label}`}
      title={disabledReason ?? `Delete ${label}`}
      disabled={Boolean(disabledReason)}
      onClick={() => {
        if (window.confirm(`Delete "${label}"? This cannot be undone.`)) onDelete();
      }}
    >
      <Trash2 className="size-4" aria-hidden="true" />
    </Button>
  );
}

/** `datetime-local` wants local `YYYY-MM-DDTHH:mm`, not an ISO string. */
function toLocalInput(value: string | null | undefined): string {
  if (!value) return '';

  const date = new Date(value);

  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/* ==========================================================================
   Pages
   ========================================================================== */

function PagesTab() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ContentStatus | ''>('');
  const [editing, setEditing] = useState<CmsPage | 'new' | null>(null);

  const pages = usePages({ limit: 50, search, ...(status ? { status } : {}) });
  const remove = useDeletePage();

  return (
    <>
      <Toolbar
        search={search}
        onSearch={setSearch}
        onCreate={() => setEditing('new')}
        createLabel="New page"
      >
        <Select
          value={status}
          onChange={(event) => setStatus(event.target.value as ContentStatus | '')}
          aria-label="Status"
          className="w-40"
        >
          <option value="">Any status</option>
          {CONTENT_STATUSES.map((value) => (
            <option key={value} value={value}>
              {value.toLowerCase()}
            </option>
          ))}
        </Select>
      </Toolbar>

      {remove.isError && (
        <div className="mb-4">
          <ErrorAlert title="Could not delete that page">{errorMessage(remove.error)}</ErrorAlert>
        </div>
      )}

      <ListShell
        isLoading={pages.isLoading}
        isError={pages.isError}
        error={pages.error}
        isEmpty={(pages.data?.items.length ?? 0) === 0}
        emptyTitle="No pages"
      >
        <Table>
          <thead>
            <tr>
              <Th>Title</Th>
              <Th>Address</Th>
              <Th>Status</Th>
              <Th>Last edited</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {pages.data?.items.map((page) => (
              <tr key={page.id} className="hover:bg-canvas/60">
                <Td className="font-medium text-ink">{page.title}</Td>
                <Td className="font-mono text-xs text-ink-subtle">/{page.slug}</Td>
                <Td>
                  <StatusBadge status={page.status} />
                </Td>
                <Td className="text-xs text-ink-subtle">{formatDateTime(page.updatedAt)}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(page)}>
                      Edit
                    </Button>
                    <DeleteButton
                      label={page.title}
                      onDelete={() => remove.mutate(page.id)}
                      disabledReason={
                        page.status === 'PUBLISHED'
                          ? 'Published — unpublish it first, or every link breaks'
                          : undefined
                      }
                    />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ListShell>

      <PageDrawer page={editing} onClose={() => setEditing(null)} />
    </>
  );
}

/**
 * Mounted only while open, keyed by id, so the form resets by unmounting rather
 * than by an effect that re-seeds it — the same pattern as the brand editor.
 *
 * The body is not in the list payload, so the form waits for the detail fetch:
 * seeding an empty textarea and filling it a moment later would let a fast
 * typist lose what they typed.
 */
function PageDrawer({ page, onClose }: { page: CmsPage | 'new' | null; onClose: () => void }) {
  const existing = page && page !== 'new' ? page : null;
  const detail = useCmsPage(existing?.id ?? null);

  if (!page) {
    return null;
  }

  return (
    <Drawer open onClose={onClose} title={existing ? 'Edit page' : 'New page'}>
      {existing && detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-48" />
        </div>
      ) : existing && detail.isError ? (
        <ErrorAlert title="Could not load this page">{errorMessage(detail.error)}</ErrorAlert>
      ) : (
        <PageForm key={existing?.id ?? 'new'} page={detail.data ?? null} onClose={onClose} />
      )}
    </Drawer>
  );
}

function PageForm({ page, onClose }: { page: CmsPage | null; onClose: () => void }) {
  const save = useSavePage();
  const existing = page;

  const [draft, setDraft] = useState({
    title: page?.title ?? '',
    slug: page?.slug ?? '',
    content: page?.content ?? '',
    status: page?.status ?? ('DRAFT' as ContentStatus),
    metaTitle: page?.metaTitle ?? '',
    metaDescription: page?.metaDescription ?? '',
  });

  const errors = fieldErrors(save.error);
  const set = (patch: Partial<typeof draft>) => setDraft((current) => ({ ...current, ...patch }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    save.mutate(
      {
        ...(existing ? { id: existing.id } : {}),
        title: draft.title,
        // Sent only when the author actually typed one: an unchanged slug field
        // would otherwise be rejected as a rename on a published page.
        ...(draft.slug && draft.slug !== existing?.slug ? { slug: draft.slug } : {}),
        content: draft.content,
        status: draft.status,
        metaTitle: draft.metaTitle || null,
        metaDescription: draft.metaDescription || null,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {save.isError && <ErrorAlert title="Could not save">{errorMessage(save.error)}</ErrorAlert>}

      <Field label="Title" htmlFor="page-title" required error={errors.title}>
        <Input
          id="page-title"
          value={draft.title}
          onChange={(event) => set({ title: event.target.value })}
          required
        />
      </Field>

      <Field
        label="Address"
        htmlFor="page-slug"
        error={errors.slug}
        hint={
          existing?.status === 'PUBLISHED'
            ? 'Published pages keep their address — unpublish to change it.'
            : 'Derived from the title when left blank.'
        }
      >
        <Input
          id="page-slug"
          value={draft.slug}
          onChange={(event) => set({ slug: event.target.value })}
          disabled={existing?.status === 'PUBLISHED'}
          placeholder="shipping-policy"
        />
      </Field>

      <Field label="Body" htmlFor="page-content" required error={errors.content} hint="Markdown.">
        <Textarea
          id="page-content"
          rows={14}
          value={draft.content}
          onChange={(event) => set({ content: event.target.value })}
          required
        />
      </Field>

      <Field label="Status" htmlFor="page-status" required>
        <Select
          id="page-status"
          value={draft.status}
          onChange={(event) => set({ status: event.target.value as ContentStatus })}
        >
          {CONTENT_STATUSES.map((value) => (
            <option key={value} value={value}>
              {value.toLowerCase()}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Meta title" htmlFor="page-meta-title" error={errors.metaTitle}>
        <Input
          id="page-meta-title"
          value={draft.metaTitle}
          onChange={(event) => set({ metaTitle: event.target.value })}
        />
      </Field>

      <Field
        label="Meta description"
        htmlFor="page-meta-description"
        error={errors.metaDescription}
      >
        <Textarea
          id="page-meta-description"
          rows={2}
          value={draft.metaDescription}
          onChange={(event) => set({ metaDescription: event.target.value })}
        />
      </Field>

      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create page'}
        </Button>
        <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/* ==========================================================================
   Blog
   ========================================================================== */

function BlogTab() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ContentStatus | ''>('');
  const [editing, setEditing] = useState<BlogPost | 'new' | null>(null);

  const posts = usePosts({ limit: 25, search, ...(status ? { status } : {}) });
  const remove = useDeletePost();

  return (
    <>
      <Toolbar
        search={search}
        onSearch={setSearch}
        onCreate={() => setEditing('new')}
        createLabel="New post"
      >
        <Select
          value={status}
          onChange={(event) => setStatus(event.target.value as ContentStatus | '')}
          aria-label="Status"
          className="w-40"
        >
          <option value="">Any status</option>
          {CONTENT_STATUSES.map((value) => (
            <option key={value} value={value}>
              {value.toLowerCase()}
            </option>
          ))}
        </Select>
      </Toolbar>

      {remove.isError && (
        <div className="mb-4">
          <ErrorAlert title="Could not delete that post">{errorMessage(remove.error)}</ErrorAlert>
        </div>
      )}

      <ListShell
        isLoading={posts.isLoading}
        isError={posts.isError}
        error={posts.error}
        isEmpty={(posts.data?.items.length ?? 0) === 0}
        emptyTitle="No posts"
      >
        <Table>
          <thead>
            <tr>
              <Th>Post</Th>
              <Th>State</Th>
              <Th>Publishes</Th>
              <Th>Views</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {posts.data?.items.map((post) => (
              <tr key={post.id} className="hover:bg-canvas/60">
                <Td>
                  <p className="font-medium text-ink">{post.title}</p>
                  <p className="font-mono text-xs text-ink-subtle">/blog/{post.slug}</p>
                  {post.tags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {post.tags.map((tag) => (
                        <Badge key={tag} tone="neutral">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </Td>
                <Td>
                  <Badge tone={STATE_TONE[post.state]}>{post.state.toLowerCase()}</Badge>
                </Td>
                <Td className="text-xs text-ink-subtle">
                  {post.publishedAt ? formatDateTime(post.publishedAt) : 'Not scheduled'}
                </Td>
                <Td className="text-sm text-ink-muted">{formatNumber(post.viewCount)}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(post)}>
                      Edit
                    </Button>
                    <DeleteButton
                      label={post.title}
                      onDelete={() => remove.mutate(post.id)}
                      disabledReason={
                        post.state === 'LIVE' ? 'Live — archive it before deleting' : undefined
                      }
                    />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ListShell>

      <PostDrawer post={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function PostDrawer({ post, onClose }: { post: BlogPost | 'new' | null; onClose: () => void }) {
  const existing = post && post !== 'new' ? post : null;
  const detail = usePost(existing?.id ?? null);

  if (!post) {
    return null;
  }

  return (
    <Drawer open onClose={onClose} title={existing ? 'Edit post' : 'New post'}>
      {existing && detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-48" />
        </div>
      ) : existing && detail.isError ? (
        <ErrorAlert title="Could not load this post">{errorMessage(detail.error)}</ErrorAlert>
      ) : (
        <PostForm key={existing?.id ?? 'new'} post={detail.data ?? null} onClose={onClose} />
      )}
    </Drawer>
  );
}

function PostForm({ post, onClose }: { post: BlogPost | null; onClose: () => void }) {
  const tags = useBlogTags();
  const save = useSavePost();
  const existing = post;

  const [draft, setDraft] = useState({
    title: post?.title ?? '',
    slug: post?.slug ?? '',
    excerpt: post?.excerpt ?? '',
    content: post?.content ?? '',
    coverImageUrl: post?.coverImageUrl ?? '',
    status: post?.status ?? ('DRAFT' as ContentStatus),
    publishedAt: toLocalInput(post?.publishedAt),
    tags: (post?.tags ?? []).join(', '),
    metaTitle: post?.metaTitle ?? '',
    metaDescription: post?.metaDescription ?? '',
  });

  const errors = fieldErrors(save.error);
  const set = (patch: Partial<typeof draft>) => setDraft((current) => ({ ...current, ...patch }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    save.mutate(
      {
        ...(existing ? { id: existing.id } : {}),
        title: draft.title,
        ...(draft.slug && draft.slug !== existing?.slug ? { slug: draft.slug } : {}),
        // Blank means "derive from the body", which the server does.
        excerpt: draft.excerpt || null,
        content: draft.content,
        coverImageUrl: draft.coverImageUrl || null,
        status: draft.status,
        publishedAt: draft.publishedAt ? new Date(draft.publishedAt).toISOString() : null,
        tags: draft.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        metaTitle: draft.metaTitle || null,
        metaDescription: draft.metaDescription || null,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {save.isError && <ErrorAlert title="Could not save">{errorMessage(save.error)}</ErrorAlert>}

      <Field label="Title" htmlFor="post-title" required error={errors.title}>
        <Input
          id="post-title"
          value={draft.title}
          onChange={(event) => set({ title: event.target.value })}
          required
        />
      </Field>

      <Field
        label="Address"
        htmlFor="post-slug"
        error={errors.slug}
        hint="Derived from the title when left blank."
      >
        <Input
          id="post-slug"
          value={draft.slug}
          onChange={(event) => set({ slug: event.target.value })}
        />
      </Field>

      <Field label="Body" htmlFor="post-content" required error={errors.content} hint="Markdown.">
        <Textarea
          id="post-content"
          rows={14}
          value={draft.content}
          onChange={(event) => set({ content: event.target.value })}
          required
        />
      </Field>

      <Field
        label="Excerpt"
        htmlFor="post-excerpt"
        error={errors.excerpt}
        hint="Left blank, the opening of the body is used."
      >
        <Textarea
          id="post-excerpt"
          rows={3}
          value={draft.excerpt}
          onChange={(event) => set({ excerpt: event.target.value })}
        />
      </Field>

      <Field
        label="Cover image"
        htmlFor="post-cover"
        error={errors.coverImageUrl}
        hint="A site path or a full https:// URL."
      >
        <Input
          id="post-cover"
          value={draft.coverImageUrl}
          onChange={(event) => set({ coverImageUrl: event.target.value })}
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Status" htmlFor="post-status" required>
          <Select
            id="post-status"
            value={draft.status}
            onChange={(event) => set({ status: event.target.value as ContentStatus })}
          >
            {CONTENT_STATUSES.map((value) => (
              <option key={value} value={value}>
                {value.toLowerCase()}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Publishes"
          htmlFor="post-published"
          error={errors.publishedAt}
          hint="A future date schedules it. Blank stamps now on publish."
        >
          <Input
            id="post-published"
            type="datetime-local"
            value={draft.publishedAt}
            onChange={(event) => set({ publishedAt: event.target.value })}
          />
        </Field>
      </div>

      <Field
        label="Tags"
        htmlFor="post-tags"
        error={errors.tags}
        hint={
          tags.data && tags.data.length > 0
            ? `In use: ${tags.data.slice(0, 8).join(', ')}`
            : 'Comma separated.'
        }
      >
        <Input
          id="post-tags"
          value={draft.tags}
          onChange={(event) => set({ tags: event.target.value })}
          placeholder="sourcing, moq"
        />
      </Field>

      <Field label="Meta title" htmlFor="post-meta-title" error={errors.metaTitle}>
        <Input
          id="post-meta-title"
          value={draft.metaTitle}
          onChange={(event) => set({ metaTitle: event.target.value })}
        />
      </Field>

      <Field
        label="Meta description"
        htmlFor="post-meta-description"
        error={errors.metaDescription}
      >
        <Textarea
          id="post-meta-description"
          rows={2}
          value={draft.metaDescription}
          onChange={(event) => set({ metaDescription: event.target.value })}
        />
      </Field>

      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create post'}
        </Button>
        <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/* ==========================================================================
   FAQs
   ========================================================================== */

function FaqsTab() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [editing, setEditing] = useState<Faq | 'new' | null>(null);

  const faqs = useFaqs({ limit: 100, search, ...(category ? { category } : {}) });
  const categories = useFaqCategories();
  const remove = useDeleteFaq();

  return (
    <>
      <Toolbar
        search={search}
        onSearch={setSearch}
        onCreate={() => setEditing('new')}
        createLabel="New FAQ"
      >
        <Select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          aria-label="Category"
          className="w-44"
        >
          <option value="">Every category</option>
          {categories.data?.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
      </Toolbar>

      {remove.isError && (
        <div className="mb-4">
          <ErrorAlert title="Could not delete that FAQ">{errorMessage(remove.error)}</ErrorAlert>
        </div>
      )}

      <ListShell
        isLoading={faqs.isLoading}
        isError={faqs.isError}
        error={faqs.error}
        isEmpty={(faqs.data?.items.length ?? 0) === 0}
        emptyTitle="No FAQs"
      >
        <Table>
          <thead>
            <tr>
              <Th>Question</Th>
              <Th>Category</Th>
              <Th>Order</Th>
              <Th>Shown</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {faqs.data?.items.map((faq) => (
              <tr key={faq.id} className="hover:bg-canvas/60">
                <Td>
                  <p className="font-medium text-ink">{faq.question}</p>
                  <p className="line-clamp-1 text-xs text-ink-subtle">{faq.answer}</p>
                </Td>
                <Td className="text-sm text-ink-muted">{faq.category ?? '—'}</Td>
                <Td className="text-sm text-ink-muted">{faq.displayOrder}</Td>
                <Td>
                  <Badge tone={faq.isActive ? 'success' : 'neutral'}>
                    {faq.isActive ? 'yes' : 'hidden'}
                  </Badge>
                </Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(faq)}>
                      Edit
                    </Button>
                    <DeleteButton label={faq.question} onDelete={() => remove.mutate(faq.id)} />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ListShell>

      <FaqDrawer faq={editing} categories={categories.data ?? []} onClose={() => setEditing(null)} />
    </>
  );
}

function FaqDrawer({
  faq,
  categories,
  onClose,
}: {
  faq: Faq | 'new' | null;
  categories: string[];
  onClose: () => void;
}) {
  if (!faq) {
    return null;
  }

  const existing = faq === 'new' ? null : faq;

  return (
    <Drawer open onClose={onClose} title={existing ? 'Edit FAQ' : 'New FAQ'}>
      <FaqForm
        key={existing?.id ?? 'new'}
        faq={existing}
        categories={categories}
        onClose={onClose}
      />
    </Drawer>
  );
}

function FaqForm({
  faq,
  categories,
  onClose,
}: {
  faq: Faq | null;
  categories: string[];
  onClose: () => void;
}) {
  const save = useSaveFaq();
  const existing = faq;

  const [draft, setDraft] = useState({
    question: faq?.question ?? '',
    answer: faq?.answer ?? '',
    category: faq?.category ?? '',
    displayOrder: String(faq?.displayOrder ?? 0),
    isActive: faq?.isActive ?? true,
  });

  const errors = fieldErrors(save.error);
  const set = (patch: Partial<typeof draft>) => setDraft((current) => ({ ...current, ...patch }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    save.mutate(
      {
        ...(existing ? { id: existing.id } : {}),
        question: draft.question,
        answer: draft.answer,
        category: draft.category || null,
        displayOrder: Number(draft.displayOrder) || 0,
        isActive: draft.isActive,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {save.isError && <ErrorAlert title="Could not save">{errorMessage(save.error)}</ErrorAlert>}

      <Field label="Question" htmlFor="faq-question" required error={errors.question}>
        <Textarea
          id="faq-question"
          rows={2}
          value={draft.question}
          onChange={(event) => set({ question: event.target.value })}
          required
        />
      </Field>

      <Field label="Answer" htmlFor="faq-answer" required error={errors.answer}>
        <Textarea
          id="faq-answer"
          rows={6}
          value={draft.answer}
          onChange={(event) => set({ answer: event.target.value })}
          required
        />
      </Field>

      <Field
        label="Category"
        htmlFor="faq-category"
        error={errors.category}
        hint={categories.length > 0 ? `In use: ${categories.join(', ')}` : 'Groups the FAQ page.'}
      >
        <Input
          id="faq-category"
          list="faq-categories"
          value={draft.category}
          onChange={(event) => set({ category: event.target.value })}
        />
        <datalist id="faq-categories">
          {categories.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
      </Field>

      <Field label="Display order" htmlFor="faq-order" error={errors.displayOrder}>
        <Input
          id="faq-order"
          type="number"
          min={0}
          value={draft.displayOrder}
          onChange={(event) => set({ displayOrder: event.target.value })}
        />
      </Field>

      <Checkbox
        label="Shown on the storefront"
        checked={draft.isActive}
        onChange={(event) => set({ isActive: event.target.checked })}
      />

      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create FAQ'}
        </Button>
        <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/* ==========================================================================
   Testimonials
   ========================================================================== */

function TestimonialsTab() {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Testimonial | 'new' | null>(null);

  const testimonials = useTestimonials({ limit: 100, search });
  const remove = useDeleteTestimonial();

  return (
    <>
      <Toolbar
        search={search}
        onSearch={setSearch}
        onCreate={() => setEditing('new')}
        createLabel="New testimonial"
      />

      {remove.isError && (
        <div className="mb-4">
          <ErrorAlert title="Could not delete that testimonial">
            {errorMessage(remove.error)}
          </ErrorAlert>
        </div>
      )}

      <ListShell
        isLoading={testimonials.isLoading}
        isError={testimonials.isError}
        error={testimonials.error}
        isEmpty={(testimonials.data?.items.length ?? 0) === 0}
        emptyTitle="No testimonials"
      >
        <Table>
          <thead>
            <tr>
              <Th>Customer</Th>
              <Th>Quote</Th>
              <Th>Rating</Th>
              <Th>Shown</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {testimonials.data?.items.map((testimonial) => (
              <tr key={testimonial.id} className="hover:bg-canvas/60">
                <Td>
                  <p className="font-medium text-ink">{testimonial.customerName}</p>
                  <p className="text-xs text-ink-subtle">
                    {[testimonial.designation, testimonial.companyName].filter(Boolean).join(', ') ||
                      '—'}
                  </p>
                </Td>
                <Td className="max-w-md">
                  <p className="line-clamp-2 text-sm text-ink-muted">{testimonial.content}</p>
                </Td>
                <Td className="whitespace-nowrap text-sm text-ink-muted">
                  {'★'.repeat(testimonial.rating)}
                  <span className="text-ink-subtle">{'★'.repeat(5 - testimonial.rating)}</span>
                </Td>
                <Td>
                  <Badge tone={testimonial.isActive ? 'success' : 'neutral'}>
                    {testimonial.isActive ? 'yes' : 'hidden'}
                  </Badge>
                </Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(testimonial)}>
                      Edit
                    </Button>
                    <DeleteButton
                      label={testimonial.customerName}
                      onDelete={() => remove.mutate(testimonial.id)}
                    />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ListShell>

      <TestimonialDrawer testimonial={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function TestimonialDrawer({
  testimonial,
  onClose,
}: {
  testimonial: Testimonial | 'new' | null;
  onClose: () => void;
}) {
  if (!testimonial) {
    return null;
  }

  const existing = testimonial === 'new' ? null : testimonial;

  return (
    <Drawer
      open
      onClose={onClose}
      title={existing ? 'Edit testimonial' : 'New testimonial'}
    >
      <TestimonialForm key={existing?.id ?? 'new'} testimonial={existing} onClose={onClose} />
    </Drawer>
  );
}

function TestimonialForm({
  testimonial,
  onClose,
}: {
  testimonial: Testimonial | null;
  onClose: () => void;
}) {
  const save = useSaveTestimonial();
  const existing = testimonial;

  const [draft, setDraft] = useState({
    customerName: testimonial?.customerName ?? '',
    companyName: testimonial?.companyName ?? '',
    designation: testimonial?.designation ?? '',
    content: testimonial?.content ?? '',
    rating: String(testimonial?.rating ?? 5),
    avatarUrl: testimonial?.avatarUrl ?? '',
    displayOrder: String(testimonial?.displayOrder ?? 0),
    isActive: testimonial?.isActive ?? true,
  });

  const errors = fieldErrors(save.error);
  const set = (patch: Partial<typeof draft>) => setDraft((current) => ({ ...current, ...patch }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    save.mutate(
      {
        ...(existing ? { id: existing.id } : {}),
        customerName: draft.customerName,
        companyName: draft.companyName || null,
        designation: draft.designation || null,
        content: draft.content,
        rating: Number(draft.rating) || 5,
        avatarUrl: draft.avatarUrl || null,
        displayOrder: Number(draft.displayOrder) || 0,
        isActive: draft.isActive,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {save.isError && <ErrorAlert title="Could not save">{errorMessage(save.error)}</ErrorAlert>}

      <Field label="Customer name" htmlFor="t-name" required error={errors.customerName}>
        <Input
          id="t-name"
          value={draft.customerName}
          onChange={(event) => set({ customerName: event.target.value })}
          required
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Company" htmlFor="t-company" error={errors.companyName}>
          <Input
            id="t-company"
            value={draft.companyName}
            onChange={(event) => set({ companyName: event.target.value })}
          />
        </Field>

        <Field label="Designation" htmlFor="t-designation" error={errors.designation}>
          <Input
            id="t-designation"
            value={draft.designation}
            onChange={(event) => set({ designation: event.target.value })}
          />
        </Field>
      </div>

      <Field label="Quote" htmlFor="t-content" required error={errors.content}>
        <Textarea
          id="t-content"
          rows={5}
          value={draft.content}
          onChange={(event) => set({ content: event.target.value })}
          required
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Rating" htmlFor="t-rating" required error={errors.rating}>
          <Select
            id="t-rating"
            value={draft.rating}
            onChange={(event) => set({ rating: event.target.value })}
          >
            {[5, 4, 3, 2, 1].map((value) => (
              <option key={value} value={value}>
                {value} ★
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Display order" htmlFor="t-order" error={errors.displayOrder}>
          <Input
            id="t-order"
            type="number"
            min={0}
            value={draft.displayOrder}
            onChange={(event) => set({ displayOrder: event.target.value })}
          />
        </Field>
      </div>

      <Field
        label="Avatar"
        htmlFor="t-avatar"
        error={errors.avatarUrl}
        hint="A site path or a full https:// URL."
      >
        <Input
          id="t-avatar"
          value={draft.avatarUrl}
          onChange={(event) => set({ avatarUrl: event.target.value })}
        />
      </Field>

      <Checkbox
        label="Shown on the storefront"
        checked={draft.isActive}
        onChange={(event) => set({ isActive: event.target.checked })}
      />

      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create testimonial'}
        </Button>
        <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
